import React, { useState } from 'react';

import { Card, FormItem, Toggle, Dropdown } from '../../../components';
import { Input } from '../../../components/Input';
import { Slider } from '../../../components/Slider';
import type { AgentForm as AgentFormType } from '../../../utilities';
import {
  turnDetectorModeOptions,
  endpointingModeOptions,
  interruptionModeOptions,
  vadSampleRateOptions,
  realtimeVadTypeOptions,
  realtimeVadEagernessOptions,
  vadActivationThresholdStepOptions,
  TURN_DETECTOR_MODE,
  REALTIME_VAD_TYPE,
  MODES_WITH_ENDPOINTING,
  MODES_WITH_INTERRUPTION,
  MODES_WITH_VAD,
} from '../const';
import {
  validateEndpointingDelays,
  validateRealtimeThreshold,
  validateModelPath,
  validateVadMinSpeechDuration,
  validateVadMinSilenceDuration,
  validateVadPrefixPaddingDuration,
  validateVadMaxBufferedSpeech,
  validateInterruptionTimeout,
  validateRealtimePrefixPaddingMs,
  validateRealtimeSilenceDurationMs,
} from '../validation/turnDetector';

export type TurnDetectorSettingsProps = {
  disabled?: boolean;
  form: AgentFormType;
};

export const TurnDetectorSettings: React.FC<TurnDetectorSettingsProps> = ({
  disabled,
  form,
}) => {
  const [showAdvancedModel, setShowAdvancedModel] = useState(false);

  const mode = form.turn_detector_mode ?? TURN_DETECTOR_MODE.MULTILINGUAL_MODEL;
  const showEndpointing = (
    MODES_WITH_ENDPOINTING as readonly string[]
  ).includes(mode);
  const showInterruption = (
    MODES_WITH_INTERRUPTION as readonly string[]
  ).includes(mode);
  const showVad = (MODES_WITH_VAD as readonly string[]).includes(mode);
  const showModel = mode === TURN_DETECTOR_MODE.MULTILINGUAL_MODEL;
  const showRealtimeVad = mode === TURN_DETECTOR_MODE.REALTIME_LLM;
  const isServerVad =
    form.turn_detector_realtime_vad_type === REALTIME_VAD_TYPE.SERVER_VAD;

  return (
    <Card
      collapsable
      size="unset"
      title="Turn Detection"
      name="agentpage.turnDetection"
      contentClassName="flex flex-col gap-y-md"
      subtitle="Configure how the voice agent detects when the user finishes speaking"
      withOverflow={false}
    >
      <FormItem<AgentFormType>
        name="turn_detector_mode"
        label="Turn Detection Mode"
      >
        <Dropdown options={turnDetectorModeOptions} disabled={disabled} />
      </FormItem>

      {showEndpointing && (
        <Card
          collapsable
          size="unset"
          subtitle="Endpointing Settings"
          name="agentpage.turnDetection.endpointing"
          contentClassName="flex flex-col gap-y-md"
        >
          <FormItem<AgentFormType>
            name="turn_detector_endpointing_mode"
            label="Endpointing Strategy"
          >
            <Dropdown options={endpointingModeOptions} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_endpointing_min_delay"
            label="Min Delay (sec)"
            validate={validateEndpointingDelays}
          >
            <Input numeric roundTo={4} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_endpointing_max_delay"
            label="Max Delay (sec)"
            validate={validateEndpointingDelays}
          >
            <Input numeric roundTo={4} disabled={disabled} />
          </FormItem>
        </Card>
      )}

      {showInterruption && (
        <Card
          collapsable
          size="unset"
          subtitle="Interruption Settings"
          name="agentpage.turnDetection.interruption"
          contentClassName="flex flex-col gap-y-md"
        >
          <FormItem<AgentFormType>
            name="turn_detector_interruption_mode"
            label="Interruption Detection Mode"
          >
            <Dropdown options={interruptionModeOptions} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_interruption_false_interruption_timeout"
            label="False Interruption Timeout (sec)"
            tooltip="Time the agent waits before classifying user speech as a real interruption. Range: 0.0 – 5.0 sec."
            validate={validateInterruptionTimeout}
          >
            <Input numeric roundTo={2} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_interruption_resume_false_interruption"
            label="Resume After False Interruption"
            className="flex justify-between items-center"
          >
            <Toggle disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_interruption_discard_audio_if_uninterruptible"
            label="Discard Audio During Uninterruptible Speech"
            className="flex justify-between items-center"
          >
            <Toggle disabled={disabled} />
          </FormItem>
        </Card>
      )}

      {showVad && (
        <Card
          collapsable
          size="unset"
          subtitle="Voice Activity Detection (VAD)"
          name="agentpage.turnDetection.vad"
          contentClassName="flex flex-col gap-y-md"
        >
          <FormItem<AgentFormType>
            name="turn_detector_vad_activation_threshold"
            label="Speech Detection Sensitivity"
            tooltip="Higher = requires louder speech (ignores noise). Lower = picks up quieter speech (may trigger on background noise)."
          >
            <Slider
              min={0.1}
              max={0.95}
              step={0.05}
              stepOptions={vadActivationThresholdStepOptions}
            />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_vad_min_speech_duration"
            label="Min Speech Duration (sec)"
            validate={validateVadMinSpeechDuration}
          >
            <Input numeric roundTo={2} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_vad_min_silence_duration"
            label="Silence Duration for End-of-Speech (sec)"
            validate={validateVadMinSilenceDuration}
          >
            <Input numeric roundTo={2} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_vad_prefix_padding_duration"
            label="Audio Padding Before Speech (sec)"
            validate={validateVadPrefixPaddingDuration}
          >
            <Input numeric roundTo={2} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_vad_max_buffered_speech"
            label="Max Buffered Speech (sec)"
            validate={validateVadMaxBufferedSpeech}
          >
            <Input numeric roundTo={1} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_vad_sample_rate"
            label="Audio Sample Rate (Hz)"
          >
            <Dropdown options={vadSampleRateOptions} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="turn_detector_vad_force_cpu"
            label="Force CPU Processing"
            className="flex justify-between items-center"
          >
            <Toggle disabled={disabled} />
          </FormItem>
        </Card>
      )}

      {showModel && (
        <Card
          collapsable
          size="unset"
          subtitle="Model Settings"
          name="agentpage.turnDetection.model"
          contentClassName="flex flex-col gap-y-md"
        >
          <div className="flex justify-start items-center">
            <span className="text-small text-mainText font-medium pr-xl mr-xl">
              Advanced Settings
            </span>
            <Toggle
              value={showAdvancedModel}
              onChange={setShowAdvancedModel}
              disabled={disabled}
            />
          </div>
          {showAdvancedModel && (
            <>
              <FormItem<AgentFormType>
                name="turn_detector_model_unlikely_threshold"
                label="Custom Confidence Threshold"
                tooltip="Override the per-language tuned threshold. Leave empty for auto-tuned defaults."
              >
                <Input numeric roundTo={4} disabled={disabled} />
              </FormItem>
              <FormItem<AgentFormType>
                name="turn_detector_model_model_path"
                label="Custom Model Path"
                tooltip="Path to a custom fine-tuned ONNX model on the server. Leave empty for default."
                validate={validateModelPath}
              >
                <Input disabled={disabled} />
              </FormItem>
            </>
          )}
        </Card>
      )}

      {showRealtimeVad && (
        <Card
          collapsable
          size="unset"
          subtitle="Realtime VAD Settings"
          name="agentpage.turnDetection.realtimeVad"
          contentClassName="flex flex-col gap-y-md"
          withOverflow={false}
        >
          <FormItem<AgentFormType>
            name="turn_detector_realtime_vad_type"
            label="Realtime VAD Type"
          >
            <Dropdown options={realtimeVadTypeOptions} disabled={disabled} />
          </FormItem>
          {!isServerVad && (
            <FormItem<AgentFormType>
              name="turn_detector_realtime_vad_eagerness"
              label="Turn Detection Eagerness"
            >
              <Dropdown
                options={realtimeVadEagernessOptions}
                disabled={disabled}
              />
            </FormItem>
          )}
          {isServerVad && (
            <>
              <FormItem<AgentFormType>
                name="turn_detector_realtime_vad_threshold"
                label="Audio Threshold"
                tooltip="Threshold for voice activity detection. Leave empty for default."
                validate={validateRealtimeThreshold}
              >
                <Input numeric roundTo={4} disabled={disabled} />
              </FormItem>
              <FormItem<AgentFormType>
                name="turn_detector_realtime_vad_prefix_padding_ms"
                label="Prefix Padding (ms)"
                tooltip="Audio buffered before the detected speech onset. Range: 0 – 2000 ms."
                validate={validateRealtimePrefixPaddingMs}
              >
                <Input numeric disabled={disabled} />
              </FormItem>
              <FormItem<AgentFormType>
                name="turn_detector_realtime_vad_silence_duration_ms"
                label="Silence Duration (ms)"
                tooltip="Duration of silence that triggers end-of-turn. Range: 0 – 5000 ms."
                validate={validateRealtimeSilenceDurationMs}
              >
                <Input numeric disabled={disabled} />
              </FormItem>
            </>
          )}
        </Card>
      )}
    </Card>
  );
};
